import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiBox,
  FiClipboard,
  FiCalendar,
  FiSettings,
  FiTrendingUp,
  FiTag,
  FiCheckCircle,
  FiAlertTriangle,
  FiBarChart2,
} from "react-icons/fi";

const ModuleAssetsManagement = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Assets Management Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Assets Management | RedERP</title>
        <meta
          name="description"
          content="Track, maintain, and manage physical assets across their lifecycle. Stay organized with depreciation, assignments, and servicing schedules."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text Section */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">Assets Management</h1>
            <p className="mb-6 text-sm sm:text-base">
              Keep your equipment, tools, and infrastructure in check. RedERP helps you manage asset lifecycles, location tracking, maintenance, and depreciation — all in one place.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>

          {/* Image Section */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/assets1.png"
              alt="Assets Management Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Feature List */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiBox size={24} />,
              title: "Asset Register",
              desc: "Record and catalog physical assets with serial numbers, cost, location, and status.",
            },
            {
              icon: <FiTag size={24} />,
              title: "Barcode & Asset Tagging",
              desc: "Generate barcodes for physical tagging and easier tracking or verification.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Asset Assignment",
              desc: "Assign assets to employees, departments, or locations with full traceability.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Maintenance Scheduling",
              desc: "Schedule routine and corrective maintenance with alerts and logs.",
            },
            {
              icon: <FiTrendingUp size={24} />,
              title: "Depreciation Tracking",
              desc: "Automate depreciation with straight-line or custom methods for accounting accuracy.",
            },
            {
              icon: <FiAlertTriangle size={24} />,
              title: "Asset Risk & Condition Monitoring",
              desc: "Log damages, service incidents, and risk scores over time.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "Asset Reports & Lifecycle",
              desc: "Access insights like total cost of ownership, asset aging, and replacement schedules.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Asset Disposal & Write-offs",
              desc: "Manage asset retirements, sales, and disposals with automated journal entries.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Custom Categories & Policies",
              desc: "Set up custom asset types, maintenance intervals, and approval policies.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Organize and Optimize Your Physical Assets</h3>
          <p className="mb-6">Start your 7-day free trial. No credit card needed.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try Assets Module Now
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleAssetsManagement;
